<template>
  <v-dialog
      v-model="isVisible"
      max-width="840px"
      persistent
  >
    <v-card :loading="isLoading">
      <v-card-title class="headline">
        Nueva tabla de cantidades
      </v-card-title>

      <v-divider></v-divider>

      <v-card-text class="pt-6">
        <v-form ref="formNewQuantityTable" :disabled="isLoading">
          <v-row>
            <v-col cols="12">
              <span class="subtitle-1">
                Ajusta los snacks y porcentajes de distribución de alimentos:
              </span>
            </v-col>
          </v-row>

          <v-row justify="space-between">
            <v-col cols="12" lg="3">
              <v-select
                  label="Snack 1 (g)"
                  :items="snackOptions"
                  v-model="newMorningSnack"
                  outlined
                  dense
                  no-data-text="No hay opciones disponibles"
                  hide-details
              />
            </v-col>
            <v-col cols="12" lg="1" align-self="center" class="text-center">
              <v-btn
                  icon
                  small
                  color="primary"
                  @click="swapSnacks()"
              >
                <v-icon>mdi-swap-horizontal</v-icon>
              </v-btn>
            </v-col>
            <v-col cols="12" lg="3">
              <v-select
                  label="Snack 2 (g)"
                  :items="snackOptions"
                  v-model="newEveningSnack"
                  outlined
                  dense
                  no-data-text="No hay opciones disponibles"
                  hide-details
              />
            </v-col>
            <v-col cols="12" lg="5" class="text-end">
              <v-chip color="primary" v-if="clientInfo.BMI">
                IMC: {{ clientInfo.BMI.toFixed(1) }} {{ BMILevel }}
              </v-chip>
            </v-col>

            <v-col cols="12" v-if="quantityTable.level > 3">
              <v-alert
                  type="info"
                  color="green"
                  outlined
                  dense
                  border="left"
                  class="mb-0"
              >
                A partir del nivel 4, considera reducir los snacks para evitar que el cliente consuma muy poco alimento.
              </v-alert>
            </v-col>

            <v-col cols="12" lg="3">
              <v-text-field
                  label="Porcentaje en desayuno (%)"
                  v-model.number="newBreakfastPercentage"
                  type="number"
                  outlined
                  dense
                  min="1"
                  :max="breakfastPercentageThreshold"
                  prefix="%"
                  reverse
                  :rules="[rules.required, rules.minPercentage]"
                  hide-details
              />
            </v-col>
            <v-col cols="12" lg="3">
              <v-text-field
                  label="Porcentaje en comida (%)"
                  v-model.number="newLunchPercentage"
                  type="number"
                  outlined
                  dense
                  min="1"
                  :max="lunchPercentageThreshold"
                  prefix="%"
                  reverse
                  :rules="[rules.required, rules.minPercentage]"
                  hide-details
              />
            </v-col>
            <v-col cols="12" lg="3">
              <v-text-field
                  label="Porcentaje en cena (%)"
                  v-model.number="newDinnerPercentage"
                  type="number"
                  outlined
                  dense
                  min="1"
                  :max="dinnerPercentageThreshold"
                  prefix="%"
                  reverse
                  :rules="[rules.required, rules.minPercentage]"
                  hide-details
              />
            </v-col>
            <v-col cols="12" lg="3" class="text-end">
              <v-chip :color="isValidPercentage && foodPercentageTotal === 100 ? 'green' : 'red'" dark>
                Total: {{ foodPercentageTotal }}%
              </v-chip>
              <span v-show="!isValidPercentage" class="red--text">
                Cada comida debe tener al menos 1%
              </span>
            </v-col>
            <v-col cols="12">
              <v-btn
                  color="green white--text"
                  @click="updateTableDetails"
                  :loading="isLoading"
                  :disabled="!isValidPercentage || foodPercentageTotal !== 100"
              >
                <v-icon left>mdi-sync</v-icon>
                Actualizar tabla
              </v-btn>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-text class="pt-5">
        <v-row>
          <v-col cols="12">
            <span class="subtitle-1">
              Vista previa de la tabla de cantidades:
            </span>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12">
            <QuantityTableComponent
                v-if="!isLoading"
                ref="printableQuantityTable"
                :clientInfo="clientInfo"
                :quantityTable="quantityTable"
            />

            <v-skeleton-loader
                v-else
                type="table-heading, list-item-two-line, article"
                :loading="isLoading"
                loadingText="Cargando vista previa de la tabla de cantidades..."
            />
          </v-col>
        </v-row>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions>
        <v-btn color="secondary" @click="closeDialog" :disabled="isLoading">
          <v-icon left>mdi-close</v-icon>
          Cerrar
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn
            color="primary"
            @click="openConfirmationDialog"
            :loading="isLoading"
            :disabled="!isValidPercentage || foodPercentageTotal !== 100"
        >
          <v-icon left>mdi-printer</v-icon>
          Imprimir
        </v-btn>
      </v-card-actions>
    </v-card>

    <DialogConfirmation
        :message="`¿Estás seguro de que deseas imprimir la tabla de cantidades para ${clientInfo.name}? No podrás hacer más cambios después de imprimir.`"
        @ok="printQuantityTable"
        ref="dialogConfirmation"
    />
  </v-dialog>
</template>

<script lang="ts">
import Vue from "vue";

import {QuantityTableInfo} from "@/services/quantityTables/entities/QuantityTableInfo";
import {ClientTableInfo} from "@/services/clients/entities/ClientTableInfo";
import {NewQuantityTable} from "@/services/quantityTables/entities/NewQuantityTable";

import DialogConfirmation from "@/components/DialogConfirmation.vue";
import QuantityTableComponent from "@/components/QuantityTableComponent.vue";

import QuantityTableServices from "@/services/quantityTables/QuantityTableServices";

export default Vue.extend({
  name: "DialogNewQuantityTable",
  components: {
    DialogConfirmation,
    QuantityTableComponent,
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    clientInfo: {
      type: Object as () => ClientTableInfo,
      required: true,
    },
    quantityTableProp: {
      type: Object as () => QuantityTableInfo,
      required: true,
    },
    hasEatenLunchOrDinner: {
      type: Boolean,
      required: true
    }
  },
  data() {
    return {
      // Options
      snackOptions: [
        {text: 'Sin snack', value: 0},
        {text: '25 g', value: 25},
        {text: '50 g', value: 50},
        {text: '75 g', value: 75},
        {text: '100 g', value: 100},
        {text: '150 g', value: 150},
      ],
      newMorningSnack: this.quantityTableProp.morningSnack,
      newEveningSnack: this.quantityTableProp.eveningSnack,

      // Client new info

      // New table
      quantityTable: {...this.quantityTableProp},
      newQuantityTable: {} as NewQuantityTable,
      newBreakfastPercentage: this.clientInfo.currentBreakfastPercentage,
      newLunchPercentage: this.clientInfo.currentLunchPercentage,
      newDinnerPercentage: this.clientInfo.currentDinnerPercentage,

      // Validation
      rules: {
        required: (value: string) => !!value || 'Campo requerido',
        minPercentage: (value: number) => {
          return (value >= 1) || 'El porcentaje debe ser al menos 1%';
        },
      },

      // Loading
      isLoading: false as boolean,
    }
  },
  computed: {
    isVisible: {
      get(): boolean {
        return this.value;
      },
    },
    breakfastPercentageThreshold(): number {
      return 100 - this.newLunchPercentage - this.newDinnerPercentage;
    },
    lunchPercentageThreshold(): number {
      return 100 - this.newBreakfastPercentage - this.newDinnerPercentage;
    },
    dinnerPercentageThreshold(): number {
      return 100 - this.newBreakfastPercentage - this.newLunchPercentage;
    },
    foodPercentageTotal(): number {
      return this.newBreakfastPercentage + this.newLunchPercentage + this.newDinnerPercentage;
    },
    isValidPercentage(): boolean {
      return this.clientInfo.currentBreakfastPercentage >= 1 &&
          this.clientInfo.currentLunchPercentage >= 1 &&
          this.clientInfo.currentDinnerPercentage >= 1;
    },
    isClassCFailed(): boolean {
      return this.quantityTable.tableClass === 'C' && this.clientInfo.lostWeightKg > 0;
    },
    BMILevel(): string {
      if (!this.clientInfo.BMI) return '';
      if (this.clientInfo.BMI < 18.5) return '(Bajo peso)';
      if (this.clientInfo.BMI >= 18.5 && this.clientInfo.BMI < 24.9) return '(Normal)';
      if (this.clientInfo.BMI >= 25 && this.clientInfo.BMI < 29.9) return '(Sobrepeso)';
      if (this.clientInfo.BMI >= 30 && this.clientInfo.BMI < 34.9) return '(Obesidad grado I)';
      if (this.clientInfo.BMI >= 35 && this.clientInfo.BMI < 39.9) return '(Obesidad grado II)';
      if (this.clientInfo.BMI >= 40) return '(Obesidad grado III)';
      return '';
    },
    resolvedType(): string {
      if (this.quantityTable.type.startsWith("WEEKLY")) return "WEEKLY";
      return this.quantityTable.type;
    }
  },
  watch: {
    quantityTableProp: {
      immediate: true,
      deep: true,
      handler(newVal: QuantityTableInfo) {
        this.quantityTable = {...newVal};
      }
    },
    quantityTable: {
      immediate: true,
      deep: true,
      handler(newVal: QuantityTableInfo) {
        this.newMorningSnack = newVal.morningSnack;
        this.newEveningSnack = newVal.eveningSnack;
      }
    },
    clientInfo: {
      immediate: true,
      deep: true,
      handler(newVal: ClientTableInfo) {
        this.newBreakfastPercentage = newVal.currentBreakfastPercentage;
        this.newLunchPercentage = newVal.currentLunchPercentage;
        this.newDinnerPercentage = newVal.currentDinnerPercentage;
      }
    }
  },
  methods: {
    async updateTableDetails() {
      this.isLoading = true;
      try {
        const body: NewQuantityTable = {
          clientId: this.clientInfo.idClient,
          breakfastPercentage: this.newBreakfastPercentage,
          lunchPercentage: this.newLunchPercentage,
          dinnerPercentage: this.newDinnerPercentage,
          tableTypeTitle: this.resolvedType,
          level: this.quantityTable.level,
          tableClass: this.quantityTable.tableClass,
          lostWeightKg: this.quantityTable.lostWeightKg,
          morningSnack: this.newMorningSnack,
          eveningSnack: this.newEveningSnack,
          decreaseFoodGrams: this.clientInfo.decreaseFoodGrams || 0,
          isClassCFailed: this.isClassCFailed,
          hasEatenLunchOrDinner: this.hasEatenLunchOrDinner,
        };

        this.quantityTable = await QuantityTableServices.updateQuantityTable(body);
      } finally {
        this.isLoading = false;
      }
    },
    openConfirmationDialog() {
      (this.$refs.dialogConfirmation as any).dialog = true;
    },
    closeDialog() {
      this.$emit('printed');
      this.$emit('input', false);
    },
    printQuantityTable() {
      const quantityTableComponent = (this.$refs.printableQuantityTable as Vue).$el as HTMLElement;
      if (!quantityTableComponent) return;

      this.printElement(quantityTableComponent, this.$vuetify);
    },
    printElement(element: HTMLElement, vuetify: any) {
      const styles = Array.from(document.querySelectorAll('link[rel="stylesheet"], style'));
      const htmlStyles = styles.map((style) => style.outerHTML).join('\n');

      const htmlContent = element.outerHTML;
      const isDark = vuetify.theme.dark;
      const themeClasses = isDark ? 'theme--dark' : 'theme--light';
      const printWindow = window.open('', '_blank', 'width=800,height=600');
      if (!printWindow) return;

      printWindow.document.write(`
        <html>
          <head>
            <title>Tabla de cantidades N${this.quantityTable.level + this.quantityTable.tableClass} - ${this.clientInfo.name}</title>
            ${htmlStyles}
            <style>
              @page {
                  padding: 10px;
                  margin: 10px;
              }
              @media print {
                * {
                  -webkit-print-color-adjust: exact !important;
                  print-color-adjust: exact !important;
                }
                body {
                  background: #fff !important;
                }
              }
            </style>
          </head>
          <body class="${themeClasses}">
            <div data-app="true" class="v-application ${themeClasses}">
              <div class="pa-4"> ${htmlContent}
              </div>
            </div>
          </body>
        </html>
      `);

      printWindow.document.close();
      setTimeout(() => {
        try {
          printWindow.document.body.focus();
          printWindow.print();
          printWindow.close();
        } catch (e) {
          console.error("Error al imprimir:", e);
          printWindow.close();
        }
      }, 1000);

      this.closeDialog();
    },
    swapSnacks() {
      const temp = this.newMorningSnack;
      this.newMorningSnack = this.newEveningSnack;
      this.newEveningSnack = temp;
    },
  }
});
</script>

<style scoped>

</style>